import { useMemo, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  RCS_ACCEPT_STATUS_ACCEPTED,
  RCS_ACCEPT_STATUS_NONE,
  RCS_CONVERSATION_STATUS_ACTIVE,
  RCS_CONVERSATION_STATUS_PENDING,
} from '@rsos/constants/rcsStatuses';
import { useGetRcsConversationMetadataQuery } from '@rsos/reduxDataStore/src/features/rcs/rcsApi';
import { useSelectedRcsConversation } from '@rsos/reduxDataStore/src/features/rcs/rcsSelectors';
import { selectUserOrgByApp } from '@rsos/sinatra';
import RcsChat from './RcsChat';
import { RcsChatContainer } from './RcsChat.styles';
import RcsChatHeader from './RcsChatHeader';
import {
  RCS_CHAT_HEADER_HEIGHT,
  CHAT_HEADER_END,
  CHAT_HEADER_ACCEPT_DECLINE,
} from './constants';

export const ICON_ROW_HEIGHT = 36;

const RcsChatBox = ({
  isRcsChatBoxVisible = false,
  onCollapseChat = () => {},
}) => {
  const [showPreconfiguredMessages, setShowPreconfiguredMessages] =
    useState(false);

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );

  const orgName = useSelector(
    state =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole.application,
      ).name,
  );

  const rcsConversation = useSelectedRcsConversation(selectedCallerID);

  useGetRcsConversationMetadataQuery(
    {
      orgName,
      callerID: selectedCallerID,
    },
    {
      skip: !selectedCallerID || rcsConversation,
    },
  );

  const handleActionButtonClick = () => {
    onCollapseChat('rcs');
  };

  const handleClickShowAccordion = useCallback(() => {
    setShowPreconfiguredMessages(prev => !prev);
  }, []);

  const showButtons = useMemo(() => {
    const STATUS_TO_BUTTON_MAP = {
      [`${RCS_ACCEPT_STATUS_NONE}${RCS_CONVERSATION_STATUS_PENDING}`]:
        CHAT_HEADER_ACCEPT_DECLINE,
      [`${RCS_ACCEPT_STATUS_ACCEPTED}${RCS_CONVERSATION_STATUS_ACTIVE}`]:
        CHAT_HEADER_END,
      [`${RCS_ACCEPT_STATUS_NONE}${RCS_CONVERSATION_STATUS_PENDING}`]:
        CHAT_HEADER_ACCEPT_DECLINE,
      [`${RCS_ACCEPT_STATUS_ACCEPTED}${RCS_CONVERSATION_STATUS_ACTIVE}`]:
        CHAT_HEADER_END,
    };

    if (!rcsConversation || !rcsConversation.data) {
      return null;
    }
    const { accept_status, conversation_status } = rcsConversation.data;
    const buttonType =
      STATUS_TO_BUTTON_MAP[`${accept_status}${conversation_status}`] || null;
    return buttonType;
  }, [rcsConversation]);

  const chatHeight = useMemo(() => {
    const height = isRcsChatBoxVisible
      ? '100%'
      : `calc(100% - ${RCS_CHAT_HEADER_HEIGHT})`;
    return height;
  }, [isRcsChatBoxVisible]);

  const containerHeight = useMemo(() => {
    // Chat has been started and not collapsed or chat has not been started and
    // the preconfigured messages accordion is shown
    const height =
      (isRcsChatBoxVisible && !showButtons) || rcsConversation
        ? '100%'
        : `${RCS_CHAT_HEADER_HEIGHT}px`;
    return height;
  }, [rcsConversation, showButtons, isRcsChatBoxVisible]);

  return (
    <RcsChatContainer
      data-name="rcs-chat-container"
      height={chatHeight}
      flex={isRcsChatBoxVisible ? '1 1 auto' : '0 0 auto'}
      maxHeight={containerHeight}
    >
      <RcsChatHeader
        isChatBoxVisible={isRcsChatBoxVisible}
        onActionButtonClick={handleActionButtonClick}
        onClickShowAccordion={handleClickShowAccordion}
        showPreconfiguredMessages={showPreconfiguredMessages}
        showButtons={showButtons}
      />

      <RcsChat
        callerID={selectedCallerID}
        showPreconfiguredMessages={showPreconfiguredMessages}
        showButtons={!!showButtons}
        isRcsChatBoxVisible={isRcsChatBoxVisible}
      />
    </RcsChatContainer>
  );
};

export default RcsChatBox;
